import type { ExtensionManifest, ExtensionProvider } from '@atlaskit/editor-common/extensions';
import {
	DefaultExtensionProvider,
	combineExtensionProviders,
} from '@atlaskit/editor-common/extensions';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { type IntlShape } from 'react-intl-next';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ExtensionHandlerWithReferenceFn } from '@atlassian/editor-referentiality';

import {
	createForgeMacroExtensionData,
	createForgeUIAnalyticsContext,
	environmentToForgeProductEnvironment,
} from '@confluence/fabric-providers/entry-points/ForgeProvider.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';

import { getConfluenceMacrosExtensionProviderForRenderer } from '@confluence/fabric-extension-lib/entry-points/getConfluenceMacrosExtensionProviderForRenderer.ts';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import type { RendererExtensionHandlers } from '@confluence/fabric-extension-lib/entry-points/extensionTypes.ts';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { fetchForgeExtensionProvider } from '@confluence/fabric-extension-handlers/entry-points/getForgeExtensionHandler.ts';
import { getWorkspaceARI } from '@confluence/forge-ui/entry-points/useWorkspaceARI.ts';
import { FlagProvider } from '@confluence/forge-ui/entry-points/flagProvider.ts';

import {
	openMacroBrowserForInitialConfiguration,
	openMacroBrowserToEditExistingMacro,
} from '../../lib/editor-extensions/index.ts';
import {
	TemplateVariableMode,
	getTemplateVariableExtensionManifest,
} from '../../lib/editor-extensions/template-variable-extension/manifest.tsx';
import { getMigrationExtensionManifest } from '../../lib/extensions-common/migration-extension/manifest.tsx';

import { useGetContextToken } from '@confluence/forge-ui/entry-points/useContextToken.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

export type ExtensionProviderOptions = {
	intl: IntlShape;
	containerId: string; // spaceKey in Next
	accountId?: string | null; // userId in Next
	contentId: string;
	cloudId: string;
	spaceKey?: string;
	spaceId?: string;
	contentType?: string;
	environment: string; // comes from sessionData
	featureFlags?: { [flag: string]: any }; // comes from sessionData
	extensionHandlers: RendererExtensionHandlers;
	showTemplateInputInPreview?: boolean;
	showTemplateVariablesInPreview?: boolean;
	setManifestReadyState?: Function;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	activationId: string;
	isLivePage?: boolean;
	isLivePagesFeatureEnabled?: boolean;
};

const createLCMRendererExtensionProvider = async () => {
	const { rendererManifest } = await import(
		/* webpackChunkName: "loadable-legacy-content-macro" */ '@confluence/experiment-legacy-content-macro'
	);
	return new DefaultExtensionProvider([rendererManifest as unknown as ExtensionManifest]);
};

/**
 * Populate Forge-based extension providers
 *
 * @param providerFactory provider factory to add extension providers into
 * @param options options to initialize extension providers
 */
export const populateExtensionProviders = (
	providerFactory: ProviderFactory,
	{
		intl,
		containerId,
		accountId,
		cloudId,
		spaceKey,
		spaceId,
		contentId,
		contentType,
		environment,
		extensionHandlers,
		showTemplateInputInPreview,
		showTemplateVariablesInPreview,
		setManifestReadyState,
		createAnalyticsEvent,
		activationId,
		isLivePage = false,
		isLivePagesFeatureEnabled = false,
	}: ExtensionProviderOptions,
): void => {
	const extensionProvidersList: (Promise<ExtensionProvider> | ExtensionProvider)[] = [];

	const extensionProvider = getConfluenceMacrosExtensionProviderForRenderer(
		{ contentId, spaceKey, spaceId, features: {} },
		extensionHandlers,
		getMigrationExtensionManifest,
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		intl,
		createAnalyticsEvent,
		providerFactory,
		isLivePagesFeatureEnabled,
	);

	extensionProvidersList.push(extensionProvider);

	const lcmExtension = createLCMRendererExtensionProvider();
	extensionProvidersList.push(lcmExtension);

	if (showTemplateInputInPreview || showTemplateVariablesInPreview) {
		extensionProvidersList.push(
			new DefaultExtensionProvider([
				getTemplateVariableExtensionManifest(
					intl,
					showTemplateInputInPreview ? TemplateVariableMode.INPUT : TemplateVariableMode.CONFIG,
				) as ExtensionManifest<any>,
			]),
		);
	}

	const apolloClient = getAGGClient();
	const ccGraphqlClient = getApolloClient();
	const contextIds = [getWorkspaceARI({ cloudId, activationId })];

	const forgeExtensionProvider = async () => {
		const getProviderOptions = ({ spaceKey: spaceKeyArg }: { spaceKey: string }) => ({
			accountId: accountId ?? undefined,
			cloudId,
			apolloClient,
			ccGraphqlClient,
			contextIds,
			dataProviders: providerFactory,
			environment: environmentToForgeProductEnvironment(environment),
			product: 'confluence',
			page: 'confluence:macroRenderer',
			isEditing: false,
			analyticsWebClient: getAnalyticsWebClient(),
			forgeUIAnalyticsContext: createForgeUIAnalyticsContext({
				objectId: contentId,
				// @ts-ignore
				objectType: contentType,
				containerId,
				containerType: 'space',
			}),
			extensionData: createForgeMacroExtensionData({
				spaceKey: spaceKeyArg ?? '',
				isLivePage,
				spaceId: spaceId ?? '',
				contentId,
				contentType,
			}),
			extensionHandlerWithReference: extensionHandlers[
				EXTENSION_TYPE.MACRO_WITH_REFERENCES
			] as ExtensionHandlerWithReferenceFn<any>,
			connectExtensionProvider: extensionProvider,
			FlagProvider,
			extensionsFilter: undefined,
			locale: intl.locale,
			useGetContextToken,
			useRouteActions,
		});

		return fetchForgeExtensionProvider({
			getProviderOptions,
			spaceKey,
		});
	};

	extensionProvidersList.push(forgeExtensionProvider());

	const combinedProvider = combineExtensionProviders(extensionProvidersList);
	providerFactory.setProvider('extensionProvider', Promise.resolve(combinedProvider));

	// Call preload on all provider that later we can use it synchronously in packages/editor/editor-common/src/extensions/extension-handlers.tsx
	void combinedProvider?.preload?.().then(() => {
		setManifestReadyState?.(true);
	});
};
